import _ from 'lodash';
import wsProxy from '@/services/ws_proxy';
import { ENDPOINTS } from '@/services/constants';
import { convertPropertyNames, snakeCasePropertyNames } from '@/utils';
import { getRelativeDateRange } from '@/utils/scheduling';

// Set filters on these columns send the chosen ids under another name.
const SET_FILTER_PARAMS = {
  'departmentName': 'department_id',
  'flagShortCodes': 'flags',
  'jobStatusName': 'job_status_id',
  'jobTypeName': 'job_type_id',
  'typeName': 'type_id'
};

const SHIFT_REPORT = {
  // Yes/no columns, only sent when a single value is chosen.
  booleanFields: ['canceled', 'overtime', 'working'],
  // Date columns, sent as `<field>_on` or `<field>_between`.
  dateFields: ['date', 'payroll_date'],
  downloadUrl: ENDPOINTS.scheduling.downloadShiftsForReporting,
  url: ENDPOINTS.scheduling.retrieveShiftsForReporting
};

const EVENT_REPORT = {
  booleanFields: [],
  dateFields: ['date'],
  downloadUrl: ENDPOINTS.scheduling.downloadEventsForReporting,
  url: ENDPOINTS.scheduling.retrieveEventsForReporting
};

/**
 * Turns the grid's paging, sorting and filters into report API parameters.
 */
function buildReportParams (criteria, config, schedulingSettings) {
  const params = new URLSearchParams();
  if ('startRow' in criteria && 'endRow' in criteria) {
    const pageSize = criteria.endRow - criteria.startRow;
    const page = Math.floor(criteria.startRow / pageSize) + 1;

    params.append('page_size', pageSize);
    params.append('page', page);
  }

  if (!_.isEmpty(criteria.sortModel)) {
    const orderBy = [];
    for (const { colId, sort } of criteria.sortModel) {
      // The API sorts by snake_case names.
      const field = _.snakeCase(colId);
      if (sort == 'asc') {
        orderBy.push(field);
      } else if (sort == 'desc') {
        orderBy.push(`-${field}`);
      }
    }
    params.append('order_by', orderBy.join(','));
  }

  if (!_.isEmpty(criteria.filterModel)) {
    for (const fieldName in criteria.filterModel) {
      const filterInfo = criteria.filterModel[fieldName];
      const snakeFieldName = _.snakeCase(fieldName);
      if (filterInfo.filterType == 'text') {
        params.append(snakeFieldName, filterInfo.filter);
      } else if (filterInfo.filterType == 'set' && filterInfo.values.length > 0) {
        if (config.booleanFields.includes(fieldName)) {
          if (filterInfo.values.length === 1) {
            params.append(snakeFieldName, filterInfo.values[0]);
          }
        } else {
          if (fieldName in SET_FILTER_PARAMS) {
            params.append(SET_FILTER_PARAMS[fieldName], filterInfo.values);
          } else {
            params.append(snakeFieldName, filterInfo.values);
          }
        }
      } else if (filterInfo.filterType == 'date' && config.dateFields.includes(snakeFieldName)) {
        switch (filterInfo.type) {
          case 'equals':
            params.append(`${snakeFieldName}_on`, filterInfo.dateFrom.slice(0, 10));
            break;
          case 'inRange':
            params.append(`${snakeFieldName}_between`, `${filterInfo.dateFrom.slice(0, 10)},${filterInfo.dateTo.slice(0, 10)}`);
            break;
          default: {
            // Relative filters (e.g. "thisWeek") are saved as is and turned into dates on every request.
            const range = getRelativeDateRange(filterInfo.type, schedulingSettings);
            if (range) {
              params.append(`${snakeFieldName}_between`, `${range.start},${range.end}`);
            }
          }
        }
      }
    }
  }

  return params;
}

/**
 * Loads a page of report rows, or downloads the whole report when `fieldsToDownload` ({field: header}) is given.
 */
function retrieveReport (rootState, { criteria, fieldsToDownload }, config) {
  return new Promise((resolve, reject) => {
    const params = buildReportParams(criteria, config, _.get(rootState, ['org', 'settings', 'scheduling'], {}));

    if (_.isEmpty(fieldsToDownload)) {
      const url = config.url + '?' + params.toString();

      wsProxy.get(url).then((response) => {
        for (let row of response.data.results) {
          convertPropertyNames(row, row);
        }
        resolve({
          rowCount: response.data.count,
          rowData: response.data.results
        });
      }).catch(error => {
        reject(error);
      });
    } else {
      const fieldsPayload = _.cloneDeep(fieldsToDownload);
      snakeCasePropertyNames(fieldsPayload);
      params.append('fields', JSON.stringify(fieldsPayload));
      const url = config.downloadUrl + '?' + params.toString();
      wsProxy.get(url).then((response) => {
        resolve(response);
      }).catch(error => {
        reject(error);
      });
    }
  });
}

/**
 * Loads a dashboard's numbers for a date range and departments (all of them when none are given).
 */
function retrieveDashboard (url, { start, end, departmentIds }) {
  return new Promise((resolve, reject) => {
    const params = new URLSearchParams();
    params.append('date_between', `${start},${end}`);
    if (!_.isEmpty(departmentIds)) {
      params.append('department_id', departmentIds.join(','));
    }
    wsProxy.get(url + '?' + params.toString()).then((response) => {
      const dashboard = {};
      convertPropertyNames(dashboard, response.data);
      resolve(dashboard);
    }).catch(error => {
      reject(error);
    });
  });
}

function initialState () {
  return {};
}

export default {
  namespaced: true,
  state: initialState,
  getters: {},
  mutations: {},
  actions: {
    retrieveEvents ({ rootState }, payload) {
      return retrieveReport(rootState, payload, EVENT_REPORT);
    },
    retrieveOpenShiftsDashboard (context, criteria) {
      return retrieveDashboard(ENDPOINTS.scheduling.retrieveOpenShiftsDashboard, criteria);
    },
    retrieveProductivityDashboard (context, criteria) {
      return retrieveDashboard(ENDPOINTS.scheduling.retrieveProductivityDashboard, criteria);
    },
    retrieveRequestsDashboard (context, criteria) {
      return retrieveDashboard(ENDPOINTS.scheduling.retrieveRequestsDashboard, criteria);
    },
    retrieveShifts ({ rootState }, payload) {
      return retrieveReport(rootState, payload, SHIFT_REPORT);
    },
    retrieveStaffingDashboard (context, criteria) {
      return retrieveDashboard(ENDPOINTS.scheduling.retrieveStaffingDashboard, criteria);
    }
  }
};
